import { storage } from '@/config';
import { IMAGE_URI } from '@/config/endpoints';

/**
 * Parameters required to build the "Added Product To Cart" event payload.
 */
export interface AddedProductToCartParams {
  productName: string;
  productId: string | undefined;
  productPrice: string | number;
  quantity: string | number;
  cartAmount: number;
  source: string;
  category?: string;
  productImageUrl?: string;
  storeUniqueSku?: string;
  pathname?: string;
  section?: string;
}

/**
 * Builds a consistent event payload for the "Added Product To Cart" CleverTap event.
 *
 * Usage:
 * pushEvent({
 *   event: 'Added Product To Cart',
 *   data: buildAddedToCartEventData({ ... }),
 * });
 */
export function buildAddedToCartEventData(params: AddedProductToCartParams) {
  const {
    productName,
    productId,
    productPrice,
    quantity,
    cartAmount,
    source,
    category,
    productImageUrl,
    storeUniqueSku,
    pathname,
    section,
  } = params;

  const data: Record<string, any> = {
    Timestamp: new Date(),
    'Product Name': productName,
    'Product ID': productId,
    'Product Price': productPrice,
    'Updated Quantity': quantity,
    'Cart Amount': cartAmount,
    Quantity: quantity,
    Source: source,
    'Store Unique SKU':
      storeUniqueSku ?? `${productId}${storage.get('store_code')}`,
  };

  if (category) {
    data['Category'] = category;
  }

  if (productImageUrl) {
    data['Product Image URL'] = productImageUrl;
  }

  if (pathname) {
    data['Pathname'] = pathname;
  }

  if (section) {
    data['Section'] = section;
  }

  return data;
}

/**
 * Extracts the product category from common product data shapes.
 *
 * It tries multiple sources in order of preference:
 * 1. `customAttributes.wz_attribute_set_id` – Wizzy-indexed products
 * 2. `extension_attributes.attribute_set_name` – Magento products
 * 3. Falls back to `undefined`
 */
export function getProductCategory(
  item: any,
  customAttributes?: Record<string, any>
): string | undefined {
  // Wizzy products
  if (customAttributes?.wz_attribute_set_id) {
    return customAttributes.wz_attribute_set_id;
  }

  // Magento products
  if (item?.extension_attributes?.attribute_set_name) {
    return item.extension_attributes.attribute_set_name;
  }

  return undefined;
}

/**
 * Resolves the effective price for a product (special price if available, else base price).
 */
export function getEffectivePrice(
  customAttributes: Record<string, any> | undefined,
  basePrice: number | string | undefined
): string | number {
  return customAttributes?.special_price
    ? customAttributes.special_price
    : (basePrice ?? 0);
}

/**
 * Builds the full CDN product image URL from a thumbnail path or custom attributes.
 *
 * Accepts either:
 * - A pre-built image URL string (returns as-is if it starts with http)
 * - Custom attributes object with a `thumbnail` field
 */
export function getProductImageUrl(
  customAttributes?: Record<string, any>
): string | undefined {
  const thumbnail = customAttributes?.thumbnail;
  if (!thumbnail) return undefined;
  return `${process.env.NEXT_PUBLIC_CDN_LINK}${IMAGE_URI}${thumbnail}`;
}
